import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./FooterWithTimer.css";
import { completeMentorSessionEventBuilder, handleButtonClickEventBuilder, sendEvent } from "../../utils/event-helpers";
import { useWithSessionData } from "../../hooks/use-with-session-data";
import { SelectedMentorContent } from "../../pages/Mentorpal/MentorPal";
import { addUrlParams } from "../../api";
import { NAME_URL_PARAM, SESSION_ID_URL_PARAM, TIMESTAMP_START_URL_PARAM } from "../../pages/Transition/Transition";

export interface FooterWithTimerProps{
  setSelectedMentorContent: (content: SelectedMentorContent) => void;
  shouldRunTimer: boolean;
  selectedMentorContent?: SelectedMentorContent;
  stopTimer: () => void;
}

const FooterWithTimer = ({
  setSelectedMentorContent,
  shouldRunTimer,
  selectedMentorContent,
  stopTimer,
}: FooterWithTimerProps) => {
  const { userId, sessionId, postSurveyTimeInSeconds, postSurveyTimeLoaded } = useWithSessionData();
  const snoozeTimeInSeconds = 5 * 60; // 5 minutes for snooze

  const [timeElapsed, setTimeElapsed] = useState(0);
  const [snoozeTimeElapsed, setSnoozeTimeElapsed] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(false);


  const navigate = useNavigate();

  // Timer effect
  useEffect(() => {
    let interval;

    if (shouldRunTimer && postSurveyTimeLoaded) {
      if (isSnoozed) {
        // Snooze timer
        if (snoozeTimeElapsed < snoozeTimeInSeconds) {
          interval = setInterval(() => {
            setSnoozeTimeElapsed((prev) => prev + 1);
          }, 1000);
        } else if (!openModal) {
          setOpenModal(true);
        }
      } else {
        // Regular timer
        if (timeElapsed < postSurveyTimeInSeconds) {
          interval = setInterval(() => {
            setTimeElapsed((prev) => prev + 1);
          }, 1000);
        } else if (!openModal) {
          setOpenModal(true);
        }
      }
    }

    return () => clearInterval(interval);
  }, [
    shouldRunTimer,
    timeElapsed,
    snoozeTimeElapsed,
    isSnoozed,
    postSurveyTimeInSeconds,
    postSurveyTimeLoaded,
    openModal,
    snoozeTimeInSeconds,
  ]);

  const formatProgress = (elapsed: number, target: number): string => {
    const count = Math.floor(elapsed / 60);
    const total = Math.floor(target / 60);
    return `${count}/${total}`;
  };

  const postSurveyTimeInMinutes = Math.floor(postSurveyTimeInSeconds / 60);

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleExtendTime = () => {
    setSnoozeTimeElapsed(0);
    setIsSnoozed(true);
    setTimeElapsed(0);
    setIsNextButtonEnabled(true);
    handleCloseModal();

    const eventData = handleButtonClickEventBuilder(userId, sessionId, "Snooze", {});
    sendEvent(eventData);
  };

  const navigateToRecommenderWithParams = () => {
    const ONE_DAY_AGO = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const navigateTarget = addUrlParams("/transition", {
      [TIMESTAMP_START_URL_PARAM]: ONE_DAY_AGO,
      ...(sessionId && { [SESSION_ID_URL_PARAM]: sessionId }),
      ...(userId && { [NAME_URL_PARAM]: userId }),
    });
    navigate(navigateTarget);
  };
  
  const handleGoToRecommendations = () => {
    handleCloseModal();
    navigateToRecommenderWithParams();
    const eventData = handleButtonClickEventBuilder(userId, sessionId, "Go To Recommendations", {});
    sendEvent(eventData);
  };

  const handlePrevious = () => {
    const eventData = completeMentorSessionEventBuilder(userId, sessionId, selectedMentorContent.mentors, {});
    sendEvent(eventData);
    setSelectedMentorContent(null);
  };

  const handleNext = () => {
    const eventData = handleButtonClickEventBuilder(userId, sessionId, "Next", {});
    sendEvent(eventData);
    stopTimer();
    navigateToRecommenderWithParams();
  };

  return (
    <Box className="footer-with-timer-container">
      <Button variant="contained" className="footer-previous-button" onClick={handlePrevious} disabled={!selectedMentorContent}>
        Back
      </Button>

      <Typography className="footer-timer">
        {isSnoozed
          ? `Snoozed For: ${formatProgress(
              snoozeTimeElapsed,
              snoozeTimeInSeconds
            )} min`
          : `Time Elapsed: ${formatProgress(
              timeElapsed,
              postSurveyTimeInSeconds
            )} min`}
      </Typography>

      <Button
      variant="contained"
        onClick={() => {
          if (isNextButtonEnabled) {
            handleNext();
          }
        }}
        disabled={!isNextButtonEnabled}
      >
        Next
      </Button>

      {/* Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Time's Up!</DialogTitle>
        <DialogContent>
          <Typography>
            You've now spent {postSurveyTimeInMinutes} minute{postSurveyTimeInMinutes > 1 ? 's' : ''} talking with mentors. Would you like to
            move on to our recommendations page?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            className="snooze-button"
            onClick={handleExtendTime}
            color="primary"
          >
            Snooze for 5 Minutes
          </Button>
          <Button
            className="recommendations-button"
            onClick={handleGoToRecommendations}
            color="primary"
          >
            Go to Recommendations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FooterWithTimer;
